/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RolesApi } from 'src/app/app-data/state/RolesApi';
import { sortArray } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { of, Observable } from 'rxjs';
import { sortRolesByName } from 'src/app/utils/array-util';
import { takeUntil, map } from 'rxjs/operators';

import { GenericDialogComponent } from 'src/app/common/generic-dialog/generic-dialog.component';
import { ColorPickerComponent } from 'src/app/common/color-picker/color-picker.component';
import { AppState } from 'src/app/services/app-state.service';
import {
	AbstractItemListEditorComponent,
	IListItemData,
	IListItemEventData,
	ItemType,
} from 'src/app/common/dialog-reusable/dialog-reusable.module';
import { ColorUtils } from 'src/app/utils/color-utils';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { DefaultSplitDataRenderType, Role } from '@touchplan/common';
import { ColorsApi } from 'src/app/app-data/state/ColorsApi';

export interface IRoleDialogData {
	rolesApi: RolesApi;
	roleColorApi: ColorsApi;
	isAdmin: boolean;
}

@Component({
	selector: 'project-role-dialog',
	templateUrl: './project-role-dialog.component.html',
	styleUrls: ['./project-role-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectRoleDialogComponent
	extends AbstractItemListEditorComponent<Role>
	implements OnInit, OnDestroy
{
	@ViewChild('colorPicker', { static: false }) public colorPicker: ColorPickerComponent;

	public rolesApi: RolesApi;
	public roleColorApi: ColorsApi;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: IRoleDialogData,
		protected dialogRef: MatDialogRef<ProjectRoleDialogComponent>,
		protected changeDetectorRef: ChangeDetectorRef,
		protected appState: AppState,
		protected dialog: MatDialog
	) {
		super(changeDetectorRef, dialogRef);

		this.rolesApi = data.rolesApi;
		this.roleColorApi = data.roleColorApi;
		this.isAdmin = data.isAdmin;

		this.self = this;
		this.itemType = {
			type: ItemType.Role,
			singularDisplay: this.strings.PROJECT_ITEM_ROLE_SINGULAR,
			pluralDisplay: this.strings.PROJECT_ITEM_ROLE_PLURAL,
			icon: 'handyman',
		};
	}

	public ngOnInit() {
		super.ngOnInit();
	}

	public ngOnDestroy() {
		super.ngOnDestroy();
	}

	public completeAdd(emittedData: IListItemEventData<Role>): boolean {
		let success = true;

		if (emittedData.type === 'Save') {
			const formData = emittedData.data;
			// role name needs to be unique
			if (this.hasLocationName(formData['name'])) {
				this.showUniquenessDialog();

				success = false;
			} else {
				const data = {
					roleName: formData['name'],
					roleColor: this.getRoleColorId(formData['color']),
				};

				this.rolesApi.addItemLegacy(data);
			}
		}

		if (success && emittedData.form) {
			emittedData.form.reset();
		}

		return success;
	}

	public completeEdit(emittedData: IListItemEventData<Role>): boolean {
		let success = true;

		if (emittedData.type === 'Delete') {
			this.rolesApi.removeItem(this.itemInEdit);
			analyticsEventsService.deletedARole(this.itemInEdit.$id, this.itemInEdit.view.roleName);
		} else if (emittedData.type === 'Save') {
			const formData = emittedData.data;

			if (this.hasLocationName(formData['name'], emittedData.item)) {
				this.showUniquenessDialog();

				success = false;
			} else {
				const data = {
					roleName: formData['name'],
					roleColor: this.getRoleColorId(formData['color']),
				};

				this.rolesApi.localUpdate(this.itemInEdit.$id, data);
				this.rolesApi.persistData(this.itemInEdit, DefaultSplitDataRenderType.database);
			}
		}

		return success;
	}

	protected createItemListObservable(): Observable<Role[]> {
		// sort members alphabatically by name
		return this.rolesApi.data.list$.pipe(
			map((roleMap) => Array.from(roleMap.values())),
			sortArray(of(sortRolesByName)),
			takeUntil(this.shutdownSubject$)
		);
	}

	public getListItemData(role: Role): IListItemData {
		if (role) {
			return {
				leftLogo: {
					type: 'color',
					value: role.view.color ? role.view.color : null,
				},
				middleContent: [role.view?.roleName ? role.view.roleName : ''],
			};
		} else {
			return null;
		}
	}

	public doesItemMatchSearch(role: Role, searchText: string): boolean {
		return (role.view && role.view.roleName ? role.view.roleName : '')
			.toLowerCase()
			.includes(searchText.toLowerCase());
	}

	protected createItemPaletteColors(): Observable<string[][]> {
		return this.roleColorApi.data.list$.pipe(
			map((colorMap) => {
				const colors: string[] = [];
				colorMap.forEach((val) => colors.push(val.view.color));
				return [colors];
			}),
			takeUntil(this.shutdownSubject$)
		);
	}

	protected createUsedColors(): Observable<{}> {
		return this.rolesApi.data.list$.pipe(
			map((roleMap) => {
				const used = {};
				roleMap.forEach((role) => {
					const color = role.view.color;
					if (color) {
						if (used[color]) {
							used[color] = used[color] + 1;
						} else {
							used[color] = 1;
						}
					}
				});
				return used;
			}),
			takeUntil(this.shutdownSubject$)
		);
	}

	protected addColor(color: string): void {
		this.roleColorApi.addColor(color);
		super.addColor(color);
	}

	private getRoleColorId(color: string): string {
		let colorId = null;
		const syncColorList = this.roleColorApi.data.syncList;

		syncColorList.forEach((syncColor) => {
			let savedColor = syncColor.view.color;

			// old saved color could be in rgb format
			if (savedColor.includes('rgb')) {
				savedColor = ColorUtils.rgbToHex(savedColor)!;
			}

			if (color === savedColor) {
				colorId = syncColor.$id;
			}
		});

		return colorId;
	}

	private hasLocationName(name: string, item: Role = null): boolean {
		// exclude item itself when comparing for edit case
		return !!Array.from(this.rolesApi.data.syncList.values()).find(
			(role) => role !== item && role.view.roleName.toLowerCase() === name.toLowerCase()
		);
	}

	private showUniquenessDialog() {
		this.dialog.open(GenericDialogComponent, {
			data: {
				title: this.strings.ROLE_NAME_UNIQUE_TEXT,
				yesButtonText: this.strings.UICONTROL_TEXT_OK,
			},
			disableClose: true,
		});
	}
}
